/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import { addMetaToQuery } from '../../mutators/add-meta';
import type {
  AccountSwitchResponse,
  BillingSSORequest,
  BillingSSOResponse,
  CancelAccountRequest,
  CreateAccountHostingPlanRequest,
  CreateAccountHostingPlanResponse,
  Error,
  FindAccountUsageParams,
  GetAccountHostingPlanResponse,
  GetAccountMeResponse,
  GetAccountUsageResponse,
  GetFeatureRequestSSOResponse,
  ListAccountInvitesParams,
  ListAccountInvitesResponse,
  ListAccountMembershipsResponse,
  ListAccountTasksParams,
  ListAccountTasksResponse,
  ListAccountsParams,
  ListMyAccountInvitesParams,
  SuccessResponse,
  UpdateAccountHostingPlanParams,
  UpdateAccountHostingPlanRequest,
  UpdateAccountHostingPlanResponse,
  UpdateAccountMeRequest,
  UpdatePasswordRequest,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Returns a one-time sign-in link to the billing area for invoices, payment methods, upgrades, and support.
 * @summary Open the billing portal
 */
export const generateAccountBillingSso = (
  billingSSORequest: BillingSSORequest,
  signal?: AbortSignal
) => {
  return customFetch<BillingSSOResponse>({
    url: `/v1/account/billing_sso`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: billingSSORequest,
    signal,
  });
};

export const getGenerateAccountBillingSsoMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateAccountBillingSso>>,
    TError,
    { data: BillingSSORequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof generateAccountBillingSso>>,
  TError,
  { data: BillingSSORequest },
  TContext
> => {
  const mutationKey = ['generateAccountBillingSso'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateAccountBillingSso>>,
    { data: BillingSSORequest }
  > = props => {
    const { data } = props ?? {};

    return generateAccountBillingSso(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/account/billing_sso` },
    {
      operationId: 'app.controllers.account_controller.generate_account_billing_sso',
      operationName: 'generateAccountBillingSso',
    }
  );

  return customOptions;
};

export type GenerateAccountBillingSsoMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateAccountBillingSso>>
>;
export type GenerateAccountBillingSsoMutationBody = BillingSSORequest;
export type GenerateAccountBillingSsoMutationError = Error;

/**
 * @summary Open the billing portal
 */
export const useGenerateAccountBillingSso = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateAccountBillingSso>>,
      TError,
      { data: BillingSSORequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof generateAccountBillingSso>>,
  TError,
  { data: BillingSSORequest },
  TContext
> => {
  const mutationOptions = getGenerateAccountBillingSsoMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Submits a cancellation request for your entire Rocket.net account. Choose immediate closure or end of the current billing period.
 * @summary Request account cancellation
 */
export const cancelAccount = (cancelAccountRequest: CancelAccountRequest, signal?: AbortSignal) => {
  return customFetch<SuccessResponse>({
    url: `/v1/account/cancel`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: cancelAccountRequest,
    signal,
  });
};

export const getCancelAccountMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelAccount>>,
    TError,
    { data: CancelAccountRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelAccount>>,
  TError,
  { data: CancelAccountRequest },
  TContext
> => {
  const mutationKey = ['cancelAccount'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelAccount>>,
    { data: CancelAccountRequest }
  > = props => {
    const { data } = props ?? {};

    return cancelAccount(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/account/cancel` },
    {
      operationId: 'app.controllers.account_controller.cancel_account',
      operationName: 'cancelAccount',
    }
  );

  return customOptions;
};

export type CancelAccountMutationResult = NonNullable<Awaited<ReturnType<typeof cancelAccount>>>;
export type CancelAccountMutationBody = CancelAccountRequest;
export type CancelAccountMutationError = Error;

/**
 * @summary Request account cancellation
 */
export const useCancelAccount = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelAccount>>,
      TError,
      { data: CancelAccountRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof cancelAccount>>,
  TError,
  { data: CancelAccountRequest },
  TContext
> => {
  const mutationOptions = getCancelAccountMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a sign-in token to view and submit product feature requests.
 * @summary Open feature requests
 */
export const generateFeatureRequestSso = (signal?: AbortSignal) => {
  return customFetch<GetFeatureRequestSSOResponse>({
    url: `/v1/account/feature_request_sso`,
    method: 'GET',
    signal,
  });
};

export const getGenerateFeatureRequestSsoQueryKey = () => {
  return [`/v1/account/feature_request_sso`] as const;
};

export const getGenerateFeatureRequestSsoQueryOptions = <
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGenerateFeatureRequestSsoQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof generateFeatureRequestSso>>> = ({
    signal,
  }) => generateFeatureRequestSso(signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    {},
    {
      url: `/v1/account/feature_request_sso`,
      operationId: 'app.controllers.account_controller.generate_feature_request_sso',
      operationName: 'generateFeatureRequestSso',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof generateFeatureRequestSso>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GenerateFeatureRequestSsoQueryResult = NonNullable<
  Awaited<ReturnType<typeof generateFeatureRequestSso>>
>;
export type GenerateFeatureRequestSsoQueryError = Error;

export function useGenerateFeatureRequestSso<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof generateFeatureRequestSso>>,
          TError,
          Awaited<ReturnType<typeof generateFeatureRequestSso>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFeatureRequestSso<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof generateFeatureRequestSso>>,
          TError,
          Awaited<ReturnType<typeof generateFeatureRequestSso>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFeatureRequestSso<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Open feature requests
 */

export function useGenerateFeatureRequestSso<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGenerateFeatureRequestSsoQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getGenerateFeatureRequestSsoSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseSuspenseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGenerateFeatureRequestSsoQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof generateFeatureRequestSso>>> = ({
    signal,
  }) => generateFeatureRequestSso(signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    {},
    {
      url: `/v1/account/feature_request_sso`,
      operationId: 'app.controllers.account_controller.generate_feature_request_sso',
      operationName: 'generateFeatureRequestSso',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof generateFeatureRequestSso>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GenerateFeatureRequestSsoSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof generateFeatureRequestSso>>
>;
export type GenerateFeatureRequestSsoSuspenseQueryError = Error;

export function useGenerateFeatureRequestSsoSuspense<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFeatureRequestSsoSuspense<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGenerateFeatureRequestSsoSuspense<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Open feature requests
 */

export function useGenerateFeatureRequestSsoSuspense<
  TData = Awaited<ReturnType<typeof generateFeatureRequestSso>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof generateFeatureRequestSso>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGenerateFeatureRequestSsoSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns your active plan, pricing, billing cycle, status, and any add-ons.
 * @summary Get your hosting plan
 */
export const findAccountHostingPlan = (signal?: AbortSignal) => {
  return customFetch<GetAccountHostingPlanResponse>({
    url: `/v1/account/hosting_plan`,
    method: 'GET',
    signal,
  });
};

export const getFindAccountHostingPlanQueryKey = () => {
  return [`/v1/account/hosting_plan`] as const;
};

export const getFindAccountHostingPlanQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountHostingPlanQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountHostingPlan>>> = ({ signal }) =>
    findAccountHostingPlan(signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    {},
    {
      url: `/v1/account/hosting_plan`,
      operationId: 'app.controllers.account_controller.find_account_hosting_plan',
      operationName: 'findAccountHostingPlan',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findAccountHostingPlan>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountHostingPlanQueryResult = NonNullable<
  Awaited<ReturnType<typeof findAccountHostingPlan>>
>;
export type FindAccountHostingPlanQueryError = Error;

export function useFindAccountHostingPlan<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountHostingPlan>>,
          TError,
          Awaited<ReturnType<typeof findAccountHostingPlan>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountHostingPlan<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountHostingPlan>>,
          TError,
          Awaited<ReturnType<typeof findAccountHostingPlan>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountHostingPlan<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get your hosting plan
 */

export function useFindAccountHostingPlan<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountHostingPlanQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindAccountHostingPlanSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountHostingPlanQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountHostingPlan>>> = ({ signal }) =>
    findAccountHostingPlan(signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    {},
    {
      url: `/v1/account/hosting_plan`,
      operationId: 'app.controllers.account_controller.find_account_hosting_plan',
      operationName: 'findAccountHostingPlan',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findAccountHostingPlan>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountHostingPlanSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findAccountHostingPlan>>
>;
export type FindAccountHostingPlanSuspenseQueryError = Error;

export function useFindAccountHostingPlanSuspense<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountHostingPlanSuspense<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountHostingPlanSuspense<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get your hosting plan
 */

export function useFindAccountHostingPlanSuspense<
  TData = Awaited<ReturnType<typeof findAccountHostingPlan>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountHostingPlan>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountHostingPlanSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Changes to a different hosting plan. Creates an order and invoice; use ?preview=true to see pricing first.
 * @summary Upgrade or change your plan
 */
export const updateAccountHostingPlan = (
  updateAccountHostingPlanRequest: UpdateAccountHostingPlanRequest,
  params?: UpdateAccountHostingPlanParams
) => {
  return customFetch<UpdateAccountHostingPlanResponse>({
    url: `/v1/account/hosting_plan`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: updateAccountHostingPlanRequest,
    params,
  });
};

export const getUpdateAccountHostingPlanMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAccountHostingPlan>>,
    TError,
    { data: UpdateAccountHostingPlanRequest; params?: UpdateAccountHostingPlanParams },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAccountHostingPlan>>,
  TError,
  { data: UpdateAccountHostingPlanRequest; params?: UpdateAccountHostingPlanParams },
  TContext
> => {
  const mutationKey = ['updateAccountHostingPlan'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAccountHostingPlan>>,
    { data: UpdateAccountHostingPlanRequest; params?: UpdateAccountHostingPlanParams }
  > = props => {
    const { data, params } = props ?? {};

    return updateAccountHostingPlan(data, params);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/account/hosting_plan` },
    {
      operationId: 'app.controllers.account_controller.update_account_hosting_plan',
      operationName: 'updateAccountHostingPlan',
    }
  );

  return customOptions;
};

export type UpdateAccountHostingPlanMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateAccountHostingPlan>>
>;
export type UpdateAccountHostingPlanMutationBody = UpdateAccountHostingPlanRequest;
export type UpdateAccountHostingPlanMutationError = Error;

/**
 * @summary Upgrade or change your plan
 */
export const useUpdateAccountHostingPlan = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateAccountHostingPlan>>,
      TError,
      { data: UpdateAccountHostingPlanRequest; params?: UpdateAccountHostingPlanParams },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateAccountHostingPlan>>,
  TError,
  { data: UpdateAccountHostingPlanRequest; params?: UpdateAccountHostingPlanParams },
  TContext
> => {
  const mutationOptions = getUpdateAccountHostingPlanMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Selects an initial hosting plan after signup. Creates an order and invoice for checkout.
 * @summary Choose your first hosting plan
 */
export const createAccountHostingPlan = (
  createAccountHostingPlanRequest: CreateAccountHostingPlanRequest,
  signal?: AbortSignal
) => {
  return customFetch<CreateAccountHostingPlanResponse>({
    url: `/v1/account/hosting_plan`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createAccountHostingPlanRequest,
    signal,
  });
};

export const getCreateAccountHostingPlanMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAccountHostingPlan>>,
    TError,
    { data: CreateAccountHostingPlanRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAccountHostingPlan>>,
  TError,
  { data: CreateAccountHostingPlanRequest },
  TContext
> => {
  const mutationKey = ['createAccountHostingPlan'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAccountHostingPlan>>,
    { data: CreateAccountHostingPlanRequest }
  > = props => {
    const { data } = props ?? {};

    return createAccountHostingPlan(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/account/hosting_plan` },
    {
      operationId: 'app.controllers.account_controller.create_account_hosting_plan',
      operationName: 'createAccountHostingPlan',
    }
  );

  return customOptions;
};

export type CreateAccountHostingPlanMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAccountHostingPlan>>
>;
export type CreateAccountHostingPlanMutationBody = CreateAccountHostingPlanRequest;
export type CreateAccountHostingPlanMutationError = Error;

/**
 * @summary Choose your first hosting plan
 */
export const useCreateAccountHostingPlan = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAccountHostingPlan>>,
      TError,
      { data: CreateAccountHostingPlanRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createAccountHostingPlan>>,
  TError,
  { data: CreateAccountHostingPlanRequest },
  TContext
> => {
  const mutationOptions = getCreateAccountHostingPlanMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * List all invites that have been sent to join this account
 * @summary Get all invites for this account
 */
export const listAccountInvites = (params?: ListAccountInvitesParams, signal?: AbortSignal) => {
  return customFetch<ListAccountInvitesResponse>({
    url: `/v1/account/invites`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListAccountInvitesQueryKey = (params?: ListAccountInvitesParams) => {
  return [`/v1/account/invites`, ...(params ? [params] : [])] as const;
};

export const getListAccountInvitesQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountInvitesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountInvites>>> = ({ signal }) =>
    listAccountInvites(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/account/invites`,
      operationId: 'app.controllers.account_controller.list_account_invites',
      operationName: 'listAccountInvites',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listAccountInvites>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountInvitesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAccountInvites>>
>;
export type ListAccountInvitesQueryError = Error;

export function useListAccountInvites<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params: undefined | ListAccountInvitesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccountInvites>>,
          TError,
          Awaited<ReturnType<typeof listAccountInvites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountInvites<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccountInvites>>,
          TError,
          Awaited<ReturnType<typeof listAccountInvites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountInvites<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get all invites for this account
 */

export function useListAccountInvites<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountInvitesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListAccountInvitesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountInvitesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountInvites>>> = ({ signal }) =>
    listAccountInvites(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/account/invites`,
      operationId: 'app.controllers.account_controller.list_account_invites',
      operationName: 'listAccountInvites',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listAccountInvites>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountInvitesSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAccountInvites>>
>;
export type ListAccountInvitesSuspenseQueryError = Error;

export function useListAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params: undefined | ListAccountInvitesParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get all invites for this account
 */

export function useListAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listAccountInvites>>,
  TError = Error,
>(
  params?: ListAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountInvitesSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Delete an invite that has not yet been accepted
 * @summary Delete an invite
 */
export const deleteAccountInvite = (inviteId: string) => {
  return customFetch<SuccessResponse>({ url: `/v1/account/invites/${inviteId}`, method: 'DELETE' });
};

export const getDeleteAccountInviteMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAccountInvite>>,
    TError,
    { inviteId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteAccountInvite>>,
  TError,
  { inviteId: string },
  TContext
> => {
  const mutationKey = ['deleteAccountInvite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteAccountInvite>>,
    { inviteId: string }
  > = props => {
    const { inviteId } = props ?? {};

    return deleteAccountInvite(inviteId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/account/invites/{inviteId}` },
    {
      operationId: 'app.controllers.account_controller.delete_account_invite',
      operationName: 'deleteAccountInvite',
    }
  );

  return customOptions;
};

export type DeleteAccountInviteMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteAccountInvite>>
>;

export type DeleteAccountInviteMutationError = Error;

/**
 * @summary Delete an invite
 */
export const useDeleteAccountInvite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteAccountInvite>>,
      TError,
      { inviteId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteAccountInvite>>,
  TError,
  { inviteId: string },
  TContext
> => {
  const mutationOptions = getDeleteAccountInviteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns your profile, plan, usage versus limits, active tasks, overdue invoices, and pending site transfers in one response.
 * @summary Get account dashboard
 */
export const findAccountMe = (signal?: AbortSignal) => {
  return customFetch<GetAccountMeResponse>({ url: `/v1/account/me`, method: 'GET', signal });
};

export const getFindAccountMeQueryKey = () => {
  return [`/v1/account/me`] as const;
};

export const getFindAccountMeQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountMe>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountMeQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountMe>>> = ({ signal }) =>
    findAccountMe(signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    {},
    {
      url: `/v1/account/me`,
      operationId: 'app.controllers.account_controller.find_account_me',
      operationName: 'findAccountMe',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findAccountMe>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountMeQueryResult = NonNullable<Awaited<ReturnType<typeof findAccountMe>>>;
export type FindAccountMeQueryError = Error;

export function useFindAccountMe<TData = Awaited<ReturnType<typeof findAccountMe>>, TError = Error>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountMe>>,
          TError,
          Awaited<ReturnType<typeof findAccountMe>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountMe<TData = Awaited<ReturnType<typeof findAccountMe>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountMe>>,
          TError,
          Awaited<ReturnType<typeof findAccountMe>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountMe<TData = Awaited<ReturnType<typeof findAccountMe>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get account dashboard
 */

export function useFindAccountMe<TData = Awaited<ReturnType<typeof findAccountMe>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountMeQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindAccountMeSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountMe>>,
  TError = Error,
>(options?: {
  query?: Partial<
    UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountMeQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountMe>>> = ({ signal }) =>
    findAccountMe(signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    {},
    {
      url: `/v1/account/me`,
      operationId: 'app.controllers.account_controller.find_account_me',
      operationName: 'findAccountMe',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findAccountMe>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountMeSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findAccountMe>>
>;
export type FindAccountMeSuspenseQueryError = Error;

export function useFindAccountMeSuspense<
  TData = Awaited<ReturnType<typeof findAccountMe>>,
  TError = Error,
>(
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountMeSuspense<
  TData = Awaited<ReturnType<typeof findAccountMe>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountMeSuspense<
  TData = Awaited<ReturnType<typeof findAccountMe>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get account dashboard
 */

export function useFindAccountMeSuspense<
  TData = Awaited<ReturnType<typeof findAccountMe>>,
  TError = Error,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountMe>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountMeSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Updates your name, email, two-factor settings, or tax ID if permitted by your role.
 * @summary Update your profile
 */
export const modifyAccountMe = (updateAccountMeRequest: UpdateAccountMeRequest) => {
  return customFetch<GetAccountMeResponse>({
    url: `/v1/account/me`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateAccountMeRequest,
  });
};

export const getModifyAccountMeMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof modifyAccountMe>>,
    TError,
    { data: UpdateAccountMeRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof modifyAccountMe>>,
  TError,
  { data: UpdateAccountMeRequest },
  TContext
> => {
  const mutationKey = ['modifyAccountMe'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof modifyAccountMe>>,
    { data: UpdateAccountMeRequest }
  > = props => {
    const { data } = props ?? {};

    return modifyAccountMe(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/account/me` },
    {
      operationId: 'app.controllers.account_controller.modify_account_me',
      operationName: 'modifyAccountMe',
    }
  );

  return customOptions;
};

export type ModifyAccountMeMutationResult = NonNullable<
  Awaited<ReturnType<typeof modifyAccountMe>>
>;
export type ModifyAccountMeMutationBody = UpdateAccountMeRequest;
export type ModifyAccountMeMutationError = Error;

/**
 * @summary Update your profile
 */
export const useModifyAccountMe = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof modifyAccountMe>>,
      TError,
      { data: UpdateAccountMeRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof modifyAccountMe>>,
  TError,
  { data: UpdateAccountMeRequest },
  TContext
> => {
  const mutationOptions = getModifyAccountMeMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Changes your Rocket.net login password. Requires your current password.
 * @summary Change your password
 */
export const changeAccountPassword = (
  updatePasswordRequest: UpdatePasswordRequest,
  signal?: AbortSignal
) => {
  return customFetch<SuccessResponse>({
    url: `/v1/account/password`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: updatePasswordRequest,
    signal,
  });
};

export const getChangeAccountPasswordMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeAccountPassword>>,
    TError,
    { data: UpdatePasswordRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof changeAccountPassword>>,
  TError,
  { data: UpdatePasswordRequest },
  TContext
> => {
  const mutationKey = ['changeAccountPassword'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof changeAccountPassword>>,
    { data: UpdatePasswordRequest }
  > = props => {
    const { data } = props ?? {};

    return changeAccountPassword(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/account/password` },
    {
      operationId: 'app.controllers.account_controller.change_account_password',
      operationName: 'changeAccountPassword',
    }
  );

  return customOptions;
};

export type ChangeAccountPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof changeAccountPassword>>
>;
export type ChangeAccountPasswordMutationBody = UpdatePasswordRequest;
export type ChangeAccountPasswordMutationError = Error;

/**
 * @summary Change your password
 */
export const useChangeAccountPassword = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof changeAccountPassword>>,
      TError,
      { data: UpdatePasswordRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof changeAccountPassword>>,
  TError,
  { data: UpdatePasswordRequest },
  TContext
> => {
  const mutationOptions = getChangeAccountPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Lists background jobs at the account level, such as creating site templates. Filter by type, status, or task ID.
 * @summary List account tasks
 */
export const listAccountTasks = (params?: ListAccountTasksParams, signal?: AbortSignal) => {
  return customFetch<ListAccountTasksResponse>({
    url: `/v1/account/tasks`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListAccountTasksQueryKey = (params?: ListAccountTasksParams) => {
  return [`/v1/account/tasks`, ...(params ? [params] : [])] as const;
};

export const getListAccountTasksQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountTasksQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountTasks>>> = ({ signal }) =>
    listAccountTasks(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/account/tasks`,
      operationId: 'app.controllers.account_controller.list_account_tasks',
      operationName: 'listAccountTasks',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listAccountTasks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountTasksQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountTasks>>>;
export type ListAccountTasksQueryError = Error;

export function useListAccountTasks<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params: undefined | ListAccountTasksParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccountTasks>>,
          TError,
          Awaited<ReturnType<typeof listAccountTasks>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountTasks<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccountTasks>>,
          TError,
          Awaited<ReturnType<typeof listAccountTasks>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountTasks<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List account tasks
 */

export function useListAccountTasks<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountTasksQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListAccountTasksSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountTasksQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountTasks>>> = ({ signal }) =>
    listAccountTasks(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/account/tasks`,
      operationId: 'app.controllers.account_controller.list_account_tasks',
      operationName: 'listAccountTasks',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listAccountTasks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountTasksSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAccountTasks>>
>;
export type ListAccountTasksSuspenseQueryError = Error;

export function useListAccountTasksSuspense<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params: undefined | ListAccountTasksParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountTasksSuspense<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountTasksSuspense<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List account tasks
 */

export function useListAccountTasksSuspense<
  TData = Awaited<ReturnType<typeof listAccountTasks>>,
  TError = Error,
>(
  params?: ListAccountTasksParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountTasks>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountTasksSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns daily visitors, disk, and bandwidth per site for a date range, plus account totals and plan limits.
 * @summary Get account usage breakdown
 */
export const findAccountUsage = (params?: FindAccountUsageParams, signal?: AbortSignal) => {
  return customFetch<GetAccountUsageResponse>({
    url: `/v1/account/usage`,
    method: 'GET',
    params,
    signal,
  });
};

export const getFindAccountUsageQueryKey = (params?: FindAccountUsageParams) => {
  return [`/v1/account/usage`, ...(params ? [params] : [])] as const;
};

export const getFindAccountUsageQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountUsageQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountUsage>>> = ({ signal }) =>
    findAccountUsage(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/account/usage`,
      operationId: 'app.controllers.account_controller.find_account_usage',
      operationName: 'findAccountUsage',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findAccountUsage>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountUsageQueryResult = NonNullable<Awaited<ReturnType<typeof findAccountUsage>>>;
export type FindAccountUsageQueryError = Error;

export function useFindAccountUsage<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params: undefined | FindAccountUsageParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountUsage>>,
          TError,
          Awaited<ReturnType<typeof findAccountUsage>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUsage<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountUsage>>,
          TError,
          Awaited<ReturnType<typeof findAccountUsage>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUsage<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get account usage breakdown
 */

export function useFindAccountUsage<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountUsageQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindAccountUsageSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountUsageQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountUsage>>> = ({ signal }) =>
    findAccountUsage(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/account/usage`,
      operationId: 'app.controllers.account_controller.find_account_usage',
      operationName: 'findAccountUsage',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findAccountUsage>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountUsageSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findAccountUsage>>
>;
export type FindAccountUsageSuspenseQueryError = Error;

export function useFindAccountUsageSuspense<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params: undefined | FindAccountUsageParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUsageSuspense<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUsageSuspense<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get account usage breakdown
 */

export function useFindAccountUsageSuspense<
  TData = Awaited<ReturnType<typeof findAccountUsage>>,
  TError = Error,
>(
  params?: FindAccountUsageParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUsage>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountUsageSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List accounts that the authenticated user is a member of
 * @summary Get account memberships
 */
export const listAccounts = (params?: ListAccountsParams, signal?: AbortSignal) => {
  return customFetch<ListAccountMembershipsResponse>({
    url: `/v1/accounts`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListAccountsQueryKey = (params?: ListAccountsParams) => {
  return [`/v1/accounts`, ...(params ? [params] : [])] as const;
};

export const getListAccountsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccounts>>,
  TError = Error,
>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccounts>>> = ({ signal }) =>
    listAccounts(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/accounts`,
      operationId: 'app.controllers.account_controller.list_accounts',
      operationName: 'listAccounts',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listAccounts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountsQueryResult = NonNullable<Awaited<ReturnType<typeof listAccounts>>>;
export type ListAccountsQueryError = Error;

export function useListAccounts<TData = Awaited<ReturnType<typeof listAccounts>>, TError = Error>(
  params: undefined | ListAccountsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccounts>>,
          TError,
          Awaited<ReturnType<typeof listAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccounts<TData = Awaited<ReturnType<typeof listAccounts>>, TError = Error>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccounts>>,
          TError,
          Awaited<ReturnType<typeof listAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccounts<TData = Awaited<ReturnType<typeof listAccounts>>, TError = Error>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get account memberships
 */

export function useListAccounts<TData = Awaited<ReturnType<typeof listAccounts>>, TError = Error>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListAccountsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccounts>>,
  TError = Error,
>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccounts>>> = ({ signal }) =>
    listAccounts(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/accounts`,
      operationId: 'app.controllers.account_controller.list_accounts',
      operationName: 'listAccounts',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listAccounts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountsSuspenseQueryResult = NonNullable<Awaited<ReturnType<typeof listAccounts>>>;
export type ListAccountsSuspenseQueryError = Error;

export function useListAccountsSuspense<
  TData = Awaited<ReturnType<typeof listAccounts>>,
  TError = Error,
>(
  params: undefined | ListAccountsParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountsSuspense<
  TData = Awaited<ReturnType<typeof listAccounts>>,
  TError = Error,
>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountsSuspense<
  TData = Awaited<ReturnType<typeof listAccounts>>,
  TError = Error,
>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get account memberships
 */

export function useListAccountsSuspense<
  TData = Awaited<ReturnType<typeof listAccounts>>,
  TError = Error,
>(
  params?: ListAccountsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountsSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * List all account invites that have been sent to the authenticated user
 * @summary Get invites sent to user
 */
export const listMyAccountInvites = (params?: ListMyAccountInvitesParams, signal?: AbortSignal) => {
  return customFetch<ListAccountInvitesResponse>({
    url: `/v1/accounts/invites`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListMyAccountInvitesQueryKey = (params?: ListMyAccountInvitesParams) => {
  return [`/v1/accounts/invites`, ...(params ? [params] : [])] as const;
};

export const getListMyAccountInvitesQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyAccountInvitesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyAccountInvites>>> = ({ signal }) =>
    listMyAccountInvites(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/accounts/invites`,
      operationId: 'app.controllers.account_controller.list_my_account_invites',
      operationName: 'listMyAccountInvites',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listMyAccountInvites>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListMyAccountInvitesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyAccountInvites>>
>;
export type ListMyAccountInvitesQueryError = Error;

export function useListMyAccountInvites<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params: undefined | ListMyAccountInvitesParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyAccountInvites>>,
          TError,
          Awaited<ReturnType<typeof listMyAccountInvites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMyAccountInvites<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyAccountInvites>>,
          TError,
          Awaited<ReturnType<typeof listMyAccountInvites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMyAccountInvites<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get invites sent to user
 */

export function useListMyAccountInvites<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListMyAccountInvitesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListMyAccountInvitesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyAccountInvitesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyAccountInvites>>> = ({ signal }) =>
    listMyAccountInvites(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/accounts/invites`,
      operationId: 'app.controllers.account_controller.list_my_account_invites',
      operationName: 'listMyAccountInvites',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listMyAccountInvites>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListMyAccountInvitesSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyAccountInvites>>
>;
export type ListMyAccountInvitesSuspenseQueryError = Error;

export function useListMyAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params: undefined | ListMyAccountInvitesParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMyAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMyAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get invites sent to user
 */

export function useListMyAccountInvitesSuspense<
  TData = Awaited<ReturnType<typeof listMyAccountInvites>>,
  TError = Error,
>(
  params?: ListMyAccountInvitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listMyAccountInvites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListMyAccountInvitesSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Generate a new account authentication token to switch the active account context for authenticated API operations
 * @summary Switch account context
 */
export const switchAccount = (accountId: string, signal?: AbortSignal) => {
  return customFetch<AccountSwitchResponse>({
    url: `/v1/accounts/${accountId}/switch`,
    method: 'POST',
    signal,
  });
};

export const getSwitchAccountMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof switchAccount>>,
    TError,
    { accountId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof switchAccount>>,
  TError,
  { accountId: string },
  TContext
> => {
  const mutationKey = ['switchAccount'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof switchAccount>>,
    { accountId: string }
  > = props => {
    const { accountId } = props ?? {};

    return switchAccount(accountId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/accounts/{accountId}/switch` },
    {
      operationId: 'app.controllers.account_controller.switch_account',
      operationName: 'switchAccount',
    }
  );

  return customOptions;
};

export type SwitchAccountMutationResult = NonNullable<Awaited<ReturnType<typeof switchAccount>>>;

export type SwitchAccountMutationError = Error;

/**
 * @summary Switch account context
 */
export const useSwitchAccount = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof switchAccount>>,
      TError,
      { accountId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof switchAccount>>,
  TError,
  { accountId: string },
  TContext
> => {
  const mutationOptions = getSwitchAccountMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
